/**
 * Общая модель плавающего окна разговора. Ею пользуются оба живых медиа-режима — звонок 1:1 в
 * чатах и групповой голос игровой сессии, — потому что это одна и та же задача: окно поверх
 * страниц, которое можно свернуть, перетащить и раскрыть на весь экран.
 *
 * Держать её отдельно от обеих фич важно не ради экономии строк, а ради единственного поведения:
 * пока правила жили в двух местах, в голосе не оказалось ни угла, ни полноэкранного режима, ни
 * защиты от второй вкладки.
 */

/** Угол экрана, в котором стоит окно на десктопе. */
export type WindowCorner = 'br' | 'bl' | 'tr' | 'tl';

/**
 * Край, к которому окно прижато на телефоне. Углов здесь нет: окно во всю ширину экрана, вбок его
 * двигать некуда, — а вот выбор между низом и верхом нужен по-настоящему. Внизу окно оказывается
 * над полем ввода сообщения, и человеку, который одновременно разговаривает и пишет, деться от
 * него было некуда.
 */
export type MobileAnchor = 'bottom' | 'top';

/** Свёрнутая пилюля, плавающее окно или весь экран. */
export type WindowMode = 'mini' | 'window' | 'full';

/** Позиционирование под каждый угол — одно на оба виджета, чтобы отступы не разъезжались. */
export const CORNER_PROPS: Record<WindowCorner, Record<string, unknown>> = {
  br: { bottom: 4, right: 4, top: 'auto', left: 'auto' },
  bl: { bottom: 4, left: 4, top: 'auto', right: 'auto' },
  tr: { top: 4, right: 4, bottom: 'auto', left: 'auto' },
  tl: { top: 4, left: 4, bottom: 'auto', right: 'auto' },
};

/** Ниже этой ширины окно не плавает, а занимает низ экрана — перетаскивать там нечего. */
export const MOBILE_BREAKPOINT_PX = 768;

/**
 * Насколько окно разговора поднято над нижним краем экрана на телефоне: безопасная зона, реальная
 * высота нижней навигации (её публикует сама навигация) и зазор в 24px — её собственный отступ
 * снизу плюс воздух до окна.
 *
 * Высота панели именно измеряется, а не записана константой: константа разъезжается от длины
 * подписей и от безопасной зоны конкретного телефона, и разъехавшаяся означает окно поверх
 * единственного способа уйти со страницы. На экранах без нижнего меню переменной нет — окно само
 * опускается к краю, а не висит над пустотой.
 */
export const MOBILE_BAR_BOTTOM =
  'calc(env(safe-area-inset-bottom, 0px) + var(--bottom-nav-height, 0px) + 24px)';

/** Тот же зазор у верхнего края: безопасная зона плюс воздух, чтобы окно не липло к чёлке. */
export const MOBILE_BAR_TOP = 'calc(env(safe-area-inset-top, 0px) + 12px)';

/**
 * Позиционирование под выбранный край экрана. Противоположная сторона обнуляется явно: без этого
 * Chakra оставляет предыдущее значение, и окно растягивается от верха до низа.
 */
export const MOBILE_ANCHOR_PROPS: Record<MobileAnchor, Record<string, unknown>> = {
  bottom: { bottom: MOBILE_BAR_BOTTOM, top: 'auto' },
  top: { top: MOBILE_BAR_TOP, bottom: 'auto' },
};

/**
 * Место, которое страница обязана оставить под свёрнутой полосой разговора.
 *
 * Это не высота полосы, а всё расстояние от нижнего края экрана до её верха: полоса стоит
 * над нижней навигацией и над безопасной зоной, и страница, оставившая место только по высоте,
 * получает полосу поверх своего последнего элемента — в чате ровно поверх поля ввода. Разница
 * невелика на глаз (безопасная зона плюс зазор), но она приходится на самый низ, где и живут
 * кнопка отправки и последняя строка ленты.
 *
 * Ноль, пока разговора нет, на десктопе и когда окно поднято к верхнему краю — там низ свободен.
 */
export const VOICE_BAR_RESERVE = 'var(--voice-bar-space, 0px)';

/**
 * С чего начинается разговор. На телефоне — со свёрнутой полосы: раскрытое окно там занимает
 * половину экрана поверх страницы, ради которой человек в приложение и зашёл. На десктопе места
 * хватает, и список участников сразу видно.
 */
export const initialWindowMode = (): WindowMode =>
  typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT_PX ? 'mini' : 'window';

/** Ближайший угол к центру окна — куда оно прилипнет, когда палец отпустят. */
export const nearestCorner = (centerX: number, centerY: number): WindowCorner =>
  `${centerY > window.innerHeight / 2 ? 'b' : 't'}${
    centerX > window.innerWidth / 2 ? 'r' : 'l'
  }` as WindowCorner;

/** Ближайший край на телефоне — по тому, в какой половине экрана окно отпустили. */
export const nearestAnchor = (centerY: number, viewportHeight: number): MobileAnchor =>
  centerY > viewportHeight / 2 ? 'bottom' : 'top';
